/**
 * Telecom / CallKit connection lifecycle — the JS-side spec for native teardown.
 *
 * Android #338 started reporting OUTGOING calls to Telecom
 * (`placeCall` → `onCreateOutgoingConnection` → `setDialing`). Pixel 10 (Ania,
 * 2026-08-27, build 105) showed the failure mode: Telecom created the
 * Connection but stripped custom extras, `extractCallId` returned null, the
 * Connection was never keyed in the map, JS `setCallDisconnected(realUuid)`
 * was a no-op, and Android kept treating her as in-call (cellular answer
 * blocked, "will end your Ascend Messenger call" warning). Hangup telemetry
 * had `native:android:telecom:onDisconnect` 45s later with **no callId**.
 *
 * Native Java/Swift must implement this state machine. These helpers are the
 * unit-tested spec; do not "simplify" a teardown path without updating both.
 */

import type { VoiceCallEndReason, VoiceCallPhase, VoiceCallSession } from '../../types/voice-call'
import { isTerminalCallPhase } from '../../stores/voice-call-store'

const UUID_DASHED =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const UUID_HEX = /^[0-9a-f]{32}$/i

/** Reconstruct a callId from a sip/tel URI SSP (dashes may have been stripped). */
export function callIdFromTelecomAddress(ssp: string | null | undefined): string | null {
  if (!ssp) return null
  const trimmed = ssp.trim().split('@')[0]?.trim() ?? ''
  if (!trimmed) return null
  if (UUID_DASHED.test(trimmed)) return trimmed.toLowerCase()
  const hex = trimmed.replace(/-/g, '')
  if (!UUID_HEX.test(hex)) return null
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ]
    .join('-')
    .toLowerCase()
}

/**
 * Resolve the callId an outgoing Telecom Connection must be keyed under.
 * Prefer extras (including the incoming-extras nested bundle some OEMs copy),
 * then the URI address, then the sole pending *outgoing* staging entry.
 */
export function resolveOutgoingTelecomCallId(input: {
  extrasCallId?: string | null
  incomingExtrasCallId?: string | null
  addressSsp?: string | null
  pendingOutgoingCallIds?: string[]
}): string | null {
  const extras = input.extrasCallId?.trim()
  if (extras) return extras
  const nested = input.incomingExtrasCallId?.trim()
  if (nested) return nested
  const fromAddress = callIdFromTelecomAddress(input.addressSsp)
  if (fromAddress) return fromAddress
  const pending = (input.pendingOutgoingCallIds ?? []).filter((id) => id.trim().length > 0)
  if (pending.length === 1) return pending[0]
  return null
}

export type TelecomConnectionState =
  | 'new'
  | 'initializing'
  | 'ringing'
  | 'dialing'
  | 'active'
  | 'holding'
  | 'disconnected'

/**
 * Safety-sweep predicate. Unkeyed connections are always zombies (JS cannot
 * drive them). When JS has no live call, still preserve RINGING incoming so a
 * cold-start FCM ring is not torn down before the session is rebuilt.
 */
export function shouldDestroySweptConnection(input: {
  callId: string | null
  state: TelecomConnectionState
  keepCallId: string | null
}): boolean {
  if (!input.callId) return true
  if (input.keepCallId && input.callId === input.keepCallId) return false
  if (input.keepCallId == null && input.state === 'ringing') return false
  return true
}

export function shouldAbortOutgoingConnection(input: {
  callId: string | null
  cancelledCallIds: readonly string[]
}): boolean {
  if (!input.callId) return true
  return input.cancelledCallIds.includes(input.callId)
}

/**
 * iOS CXStartCallAction / reportOutgoingCall must not proceed after JS hangup
 * already dropped the uuid mapping (otherwise CallKit reports a live outgoing
 * call with no owner — the same class of leak as Android's unkeyed Connection).
 */
export function shouldReportOutgoingCallKit(input: {
  uuid: string
  mappedUuid: string | null
}): boolean {
  return input.mappedUuid === input.uuid
}

/**
 * PHANTOM-RING GUARD, caller side (call 64b19aeb, Zach→Adele 2026-10-07):
 * Android froze the caller's WebView for 11 minutes inside startCall's
 * permission probe (app backgrounded mid-dial while driving). On resume the
 * flow continued exactly where it stopped and published the INVITE as if no
 * time had passed — the callee rang for a call the caller had abandoned long
 * before, answered within 6s, and got a 2-second ghost connection. An INVITE
 * may only be published while the originating session is still THIS call's
 * live 'outgoing' phase and the dial is younger than the ring window.
 */
export const STALE_OUTGOING_INVITE_MS = 60_000

export function shouldAbortStaleOutgoingInvite(input: {
  session: Pick<VoiceCallSession, 'callId' | 'phase' | 'startedAt'> | null | undefined
  callId: string
  now: number
}): boolean {
  if (!input.session) return true
  if (input.session.callId !== input.callId) return true
  if (input.session.phase !== 'outgoing') return true
  return input.now - (input.session.startedAt ?? 0) > STALE_OUTGOING_INVITE_MS
}

/**
 * PHANTOM-RING GUARD, callee side — the display-time gate for an INVITE data
 * push (Android AscendMessagingService mirrors this; it runs with the WebView
 * dead, so it cannot use the JS tombstones):
 *
 *  - 'tombstoned': this device already saw this callId end (the caller's
 *    CANCELLED / MISSED / remote-END push arrived before or without the
 *    INVITE — FCM does not guarantee ordering, and redelivery after a
 *    reconnect can replay an old invite). Ringing it is a phantom call.
 *    A room re-add reuses the room id, so a room invite is 'tombstoned'
 *    only when the server ended that call (remoteEndedCallIds). Android
 *    additionally orders room invites by send time against that terminal
 *    push, so a re-add after a ring timeout rings
 *    (TelecomCallManager.shouldIgnoreTombstonedInvite).
 *  - 'stale': the push's FCM server send-time (RemoteMessage.getSentTime) is
 *    older than the ring window. The send-side `ttl: '50s'` bounds FCM
 *    store-and-forward, but a queued/replayed push can still surface late —
 *    by then the caller gave up and any END push may already be gone.
 */
export const INVITE_PUSH_MAX_AGE_MS = 60_000

export function shouldIgnoreInvitePush(input: {
  callId: string | null
  endedCallIds: readonly string[]
  /** FCM server timestamp in ms; 0/null = unknown (old FCM) → never stale. */
  sentTimeMs: number | null
  nowMs: number
  roomInvite?: boolean
  remoteEndedCallIds?: readonly string[]
}): 'tombstoned' | 'stale' | null {
  const tombstones = input.roomInvite ? (input.remoteEndedCallIds ?? []) : input.endedCallIds
  if (input.callId && tombstones.includes(input.callId)) return 'tombstoned'
  if (
    input.sentTimeMs != null &&
    input.sentTimeMs > 0 &&
    input.nowMs - input.sentTimeMs > INVITE_PUSH_MAX_AGE_MS
  ) {
    return 'stale'
  }
  return null
}

/**
 * How long a natively-answered call (Telecom onAnswer: car, Bluetooth, lock
 * screen, notification) is protected from the orphan sweep while JS rebuilds
 * its session.
 */
export const NATIVE_ANSWER_SWEEP_GRACE_MS = 30_000

/**
 * CallId native sweep may keep; null means "destroy non-ringing orphans".
 *
 * A native answer promotes the Connection to ACTIVE before JS has a session
 * for it. Overlapping foreground passes (visibilitychange + resume) meant one
 * pass swept with no session and destroyed that ACTIVE car-answered call
 * ~0.5s after the answer (dcfc5c9a, 2026-10-08) — Android Auto lost the call.
 * So a callId answered natively within the grace window is kept, unless the
 * session already shows that call as over.
 */
export function liveCallIdForNativeSweep(
  session: Pick<VoiceCallSession, 'callId' | 'phase'> | null | undefined,
  recentNativeAnswer?: { callId: string; atMs: number } | null,
  nowMs: number = Date.now(),
): string | null {
  if (session?.callId && !isTerminalCallPhase(session.phase) && session.phase !== 'elsewhere') {
    return session.callId
  }
  if (!recentNativeAnswer?.callId) return null
  if (nowMs - recentNativeAnswer.atMs > NATIVE_ANSWER_SWEEP_GRACE_MS) return null
  if (session?.callId === recentNativeAnswer.callId) return null
  return recentNativeAnswer.callId
}

export type NativeDisconnectReason = 'rejected' | 'missed' | 'canceled' | 'consent'

/**
 * Telecom/CallKit disconnect reason for a JS-side call end. Mirrors
 * AscendConnection.markDisconnectedFromJs: `consent` (Android only) becomes a
 * labelled REMOTE DisconnectCause so the system and car call UI show why the
 * call ended. iOS: `canceled` only while still ringing — after a CallKit
 * answer it reads as a spurious ring-dismiss and can be suppressed.
 */
export function telecomDisconnectReasonForEnd(
  reason: VoiceCallEndReason,
  phase: VoiceCallPhase,
  platform: 'ios' | 'android' | 'web' | null,
): NativeDisconnectReason | undefined {
  if (reason === 'declined') return 'rejected'
  if (reason === 'missed') return 'missed'
  if (reason === 'cancelled' && phase === 'incoming') return 'canceled'
  if (reason === 'consent-missing' && platform === 'android') return 'consent'
  return undefined
}

/**
 * Where a call end came from — carried on `ctrl:hangUp` telemetry so a
 * field report can tell an in-app End from a car / Telecom / native teardown.
 */
export type CallEndSource =
  | 'in-app'
  | 'car'
  | 'telecom'
  | 'notification'
  | 'lock-screen'
  | 'native-teardown'
  | 'timeout'
  | 'remote'

const CALL_END_SOURCES: ReadonlySet<string> = new Set<CallEndSource>([
  'in-app',
  'car',
  'telecom',
  'notification',
  'lock-screen',
  'native-teardown',
  'timeout',
  'remote',
])

export function isCallEndSource(value: unknown): value is CallEndSource {
  return typeof value === 'string' && CALL_END_SOURCES.has(value)
}

/**
 * Native callAction `end` sources meaning the PEER ended the call:
 *  - `remote-ended`: iOS VoIP DISMISS push flagged remoteEnded — the server
 *    already has the peer's END, so publishing another is the duplicate-END
 *    half of the 2026-10-21 report (`ctrl:hangUp endSource=telecom`).
 *  - `remote-left`: iOS native-media backstop — the 1:1 peer left the Chime
 *    meeting and nothing (rejoin / AppSync END) followed within
 *    {@link NATIVE_REMOTE_LEFT_BACKSTOP_MS}. Nothing confirmed an END on the
 *    server, so this one publishes a peer-lost END (duplicates are dropped
 *    server-side) to settle the session and history.
 */
export const NATIVE_REMOTE_END_SOURCES: ReadonlySet<string> = new Set(['remote-ended', 'remote-left'])

/** Map a native callAction `source` to the hang-up end source. */
export function endSourceFromNativeAction(source: string | null | undefined): CallEndSource {
  if (source === 'car') return 'car'
  if (source === 'notification') return 'notification'
  if (source === 'lock-screen') return 'lock-screen'
  if (source === 'watchdog') return 'native-teardown'
  if (source && NATIVE_REMOTE_END_SOURCES.has(source)) return 'remote'
  return 'telecom'
}

/**
 * A native `end` callAction (CallKit / Telecom End, car, notification, or a
 * native remote-end) for `callId`:
 *  - 'no-session': JS has no live session for it (tombstone it instead).
 *  - 'ignore': already over here.
 *  - 'finish-remote': the server already ended it — end locally, publish
 *    NOTHING (`remote-ended`; also `remote-left` on a session JS never
 *    accepted, where an END/REJECT would misreport an answered call).
 *  - 'finish-peer-lost': `remote-left` — end locally and publish a peer-lost
 *    END, exactly like JS's own remote-left grace path.
 *  - 'decline' / 'hang-up': a local end — REJECT / END goes to the peer.
 */
export function routeNativeEndAction(input: {
  session: Pick<VoiceCallSession, 'callId' | 'phase'> | null | undefined
  callId: string
  source?: string | null
}): 'no-session' | 'ignore' | 'finish-remote' | 'finish-peer-lost' | 'decline' | 'hang-up' {
  const { session, callId, source } = input
  if (!session || session.callId !== callId) return 'no-session'
  if (isTerminalCallPhase(session.phase)) return 'ignore'
  if (source === 'remote-left' && session.phase !== 'incoming') return 'finish-peer-lost'
  if (source && NATIVE_REMOTE_END_SOURCES.has(source)) return 'finish-remote'
  if (session.phase === 'incoming') return 'decline'
  return 'hang-up'
}

/**
 * iOS native-media backstop: the 1:1 peer's attendee left the native Chime
 * meeting. A live JS ends the call itself (its 5 s peer-lost grace, or the
 * AppSync END); with the WebView suspended nothing would, so native ends the
 * call locally as `remote-left` once this long has passed with no rejoin and
 * no END. Mirrors `remoteLeftBackstopSeconds` in ChimeMediaController.swift.
 */
export const NATIVE_REMOTE_LEFT_BACKSTOP_MS = 10_000

/**
 * The backstop timer's decision when it fires. The Swift implementation is
 * `ChimeMediaController.remoteLeftBackstopDecision(callId:)` — change both
 * together (same inputs, same order, same labels).
 *
 * 'rearm:local-reconnecting' = this phone's own Chime session is
 * reconnecting, so the peer may only look absent: wait another period.
 */
export function shouldFireRemoteLeftBackstop(input: {
  /** The native meeting still belongs to this call. */
  callStillLive: boolean
  /** A remote attendee is back (rejoin / reconnect). */
  remotePresent: boolean
  /** Flagged group/room call (JS join flag or a room invite). */
  isGroup: boolean
  /** Distinct remote users seen in this meeting (≥ 2 = group). */
  distinctRemoteUsers: number
  /** This phone's own Chime audio session is reconnecting. */
  localReconnecting: boolean
  /**
   * Phone scene foreground-active (`PhoneScene.state`, not the app state a
   * CarPlay scene can hold active): JS is running and owns the decision.
   */
  appActive: boolean
}):
  | 'fire'
  | 'rearm:local-reconnecting'
  | 'skip:call-gone'
  | 'skip:peer-back'
  | 'skip:group'
  | 'skip:app-active' {
  if (!input.callStillLive) return 'skip:call-gone'
  if (input.remotePresent) return 'skip:peer-back'
  if (input.isGroup || input.distinctRemoteUsers > 1) return 'skip:group'
  if (input.localReconnecting) return 'rearm:local-reconnecting'
  if (input.appActive) return 'skip:app-active'
  return 'fire'
}

type DismissSession = Pick<VoiceCallSession, 'callId' | 'phase' | 'callerId' | 'localRole'>

/**
 * Native "dismiss" (notification swipe / shade clear / Telecom onAbort).
 * Only a genuine INCOMING ring is abandoned locally (no peer notify — the
 * user's other devices keep ringing). A dismiss that reaches a live outgoing
 * / connecting / active call — or any call we placed — is a hang-up: the old
 * path tore down the native call and left the JS call running (2026-10-19
 * Android Auto field test), so the peer never got END/CANCELLED.
 */
export function routeNativeDismiss(
  session: DismissSession | null | undefined,
  callId: string,
  currentUserId?: string | null,
): 'abandon-ring' | 'hang-up' {
  if (!session || session.callId !== callId || isTerminalCallPhase(session.phase)) {
    return 'abandon-ring'
  }
  const weAreCaller =
    session.localRole === 'caller' ||
    (Boolean(currentUserId) && session.callerId === currentUserId)
  if (session.phase === 'incoming' && !weAreCaller) return 'abandon-ring'
  if (session.phase === 'elsewhere') return 'abandon-ring'
  return 'hang-up'
}

/**
 * Native teardown origins that are housekeeping or JS-initiated — JS already
 * owns the outcome (or the connection was not JS's live call).
 */
export const NATIVE_HOUSEKEEPING_TEARDOWN_SOURCES: ReadonlySet<string> = new Set([
  'js',
  'sweep',
  'other-call',
  'unkeyed',
  'refused',
  'user-cleared',
])

/**
 * A Telecom connection was destroyed natively. A still-live JS call for that
 * callId must not keep running without it:
 *  - 'hang-up': a user/system end (abort, disconnect, reject, dialing stall,
 *    notification, ring timeout) — END/CANCELLED goes to the peer.
 *  - 'finish-remote': the server's remote END arrived natively (FCM) — end
 *    locally without re-publishing.
 *  - 'ignore': housekeeping / JS-initiated, another call, a genuine incoming
 *    ring (its dismiss/decline action covers it), or no live call.
 */
export function decideOnNativeConnectionDestroyed(input: {
  session: Pick<VoiceCallSession, 'callId' | 'phase'> | null | undefined
  callId: string
  source: string
}): 'hang-up' | 'finish-remote' | 'ignore' {
  const { session, callId, source } = input
  if (!session || session.callId !== callId) return 'ignore'
  if (isTerminalCallPhase(session.phase) || session.phase === 'elsewhere') return 'ignore'
  if (NATIVE_HOUSEKEEPING_TEARDOWN_SOURCES.has(source)) return 'ignore'
  if (session.phase === 'incoming') return 'ignore'
  if (source === 'fcm') return 'finish-remote'
  return 'hang-up'
}

/**
 * Liveness re-check after an await in the outgoing dial (permission probe,
 * dial-plan prep): only the still-live 'outgoing' session for THIS callId
 * may start the native ongoing call. Without it a dial the user already
 * hung up (car End while the WebView was busy) re-created a live Telecom
 * call (2026-10-19).
 */
export function isOutgoingDialStillLive(
  session: Pick<VoiceCallSession, 'callId' | 'phase'> | null | undefined,
  callId: string,
  isTombstoned: (callId: string) => boolean = () => false,
): boolean {
  if (!session || session.callId !== callId) return false
  if (session.phase !== 'outgoing') return false
  return !isTombstoned(callId)
}

/** Final 1:1 Telecom label when no real name is known (never "Care team"). */
export const ONE_TO_ONE_TELECOM_FALLBACK_NAME = 'Ascend call'
/** A genuine group / care-team room label. */
export const GROUP_TELECOM_FALLBACK_NAME = 'Care team'

/**
 * Name handed to the OS call surfaces (Android Telecom → Android Auto /
 * Bluetooth / chip). A 1:1 call shows the PERSON; generic placeholders fall
 * through to the directory / recents name and finally 'Ascend call'. Only a
 * group call may use the 'Care team' room label.
 */
export function resolveTelecomPeerName(input: {
  displayName?: string | null
  isGroup?: boolean
  directoryName?: string | null
  isPlaceholder: (name: string | null | undefined) => boolean
}): string {
  const candidates = [input.displayName, input.directoryName]
  for (const name of candidates) {
    const trimmed = name?.trim()
    if (trimmed && !input.isPlaceholder(trimmed) && !isGenericTelecomName(trimmed)) return trimmed
  }
  return input.isGroup ? GROUP_TELECOM_FALLBACK_NAME : ONE_TO_ONE_TELECOM_FALLBACK_NAME
}

function isGenericTelecomName(name: string): boolean {
  const lower = name.toLowerCase()
  return (
    lower === 'care team' ||
    lower === 'ascend call' ||
    lower === 'ascend messenger' ||
    lower === 'ongoing call' ||
    lower === 'call in progress'
  )
}

/**
 * Caller-side late ACCEPT for a tombstoned call: answer with END only when
 * this device abandoned the dial before it connected (cancelled / missed) and
 * the ACCEPT is a plain 1:1 answer. Room ACCEPTs reuse the room callId; an END
 * there would close a live group room for everyone still in it.
 */
export function shouldEndLateAccept(input: {
  tombstoneReason: string | null | undefined
  roomInvite?: boolean | null
  roomCallId?: string | null
  joinCallId?: string | null
}): boolean {
  if (input.roomInvite || input.roomCallId || input.joinCallId) return false
  const reason = String(input.tombstoneReason ?? '').toLowerCase()
  return reason === 'cancelled' || reason === 'missed'
}

/**
 * Keep-alive gate: a 1:1 call this device already ended (JS tombstone) or that
 * native refused as tombstoned must not be re-anchored — send END instead.
 * Group rooms reuse their callId for every (re-)join (a participant re-added
 * within the tombstone window), so a group keep-alive is never refused.
 */
export function shouldEndInsteadOfKeepAlive(input: {
  callId: string | null | undefined
  isGroup?: boolean
  isTombstoned: (callId: string) => boolean
  nativeRefusal?: { refused: boolean; reason?: string } | null
}): boolean {
  if (!input.callId || input.isGroup) return false
  if (input.isTombstoned(input.callId)) return true
  return Boolean(input.nativeRefusal?.refused && input.nativeRefusal.reason === 'tombstoned')
}
